import { useCallback, useMemo, useState } from "react";

import {
  createContactImportTask,
  downloadContactImportTemplate,
  downloadContactImportUnimported,
  executeContactImport,
  parseContactImportFile,
  parseContactImportPaste,
  saveContactImportRows,
} from "../repo/crmContactImportRepo";
import {
  applyContactImportExecutionResults,
  buildEditedRowsPayload,
  isContactImportRowExecutable,
  selectExecutableRowIds,
  updateContactImportCell,
} from "../service/crmContactImportService";
import type {
  ContactImportAction,
  ContactImportExecutionResult,
  ContactImportRow,
  ContactImportTask,
} from "../types";

/** 联系人批量导入页面运行时。 */
export function useCrmContactImportRuntime() {
  const [task, setTask] = useState<ContactImportTask | null>(null);
  const [rows, setRows] = useState<ContactImportRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ContactImportExecutionResult | null>(null);

  const applyTask = useCallback((nextTask: ContactImportTask) => {
    setTask(nextTask);
    setRows(nextTask.rows);
    setResult(null);
  }, []);

  const run = useCallback(async <T,>(action: () => Promise<T>): Promise<T | null> => {
    setBusy(true);
    setError(null);
    try {
      return await action();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "联系人导入操作失败");
      return null;
    } finally {
      setBusy(false);
    }
  }, []);

  const uploadFile = useCallback(
    async (file: File) => {
      await run(async () => {
        const created = await createContactImportTask("file");
        const parsed = await parseContactImportFile(created.id, created.version, file);
        applyTask(parsed);
        return parsed;
      });
    },
    [applyTask, run],
  );

  const parsePaste = useCallback(
    async (html: string | null, text: string) => {
      await run(async () => {
        const created = await createContactImportTask("paste");
        const parsed = await parseContactImportPaste(created.id, created.version, html, text);
        applyTask(parsed);
        return parsed;
      });
    },
    [applyTask, run],
  );

  const saveRows = useCallback(async () => {
    if (!task) return;
    await run(async () => {
      const saved = await saveContactImportRows(
        task.id,
        buildEditedRowsPayload(task.version, rows),
      );
      applyTask(saved);
      return saved;
    });
  }, [applyTask, rows, run, task]);

  const execute = useCallback(async () => {
    if (!task) return;
    await run(async () => {
      if (!task.previewDigest) throw new Error("内容已修改，请先保存并重新校验");
      const rowIds = selectExecutableRowIds(rows);
      if (!rowIds.length) throw new Error("没有可导入的有效行");
      const response = await executeContactImport(task, rowIds);
      setRows((current) => applyContactImportExecutionResults(current, response));
      setTask((current) =>
        current
          ? { ...current, status: response.status, version: current.version + 1 }
          : current,
      );
      setResult(response);
      return response;
    });
  }, [rows, run, task]);

  const downloadTemplate = useCallback(async () => {
    await run(async () => {
      const blob = await downloadContactImportTemplate();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "联系人批量导入模板-v1.xlsx";
      anchor.click();
      URL.revokeObjectURL(url);
      return blob;
    });
  }, [run]);

  const downloadUnimportedRows = useCallback(async () => {
    if (!task) return;
    await run(async () => {
      const blob = await downloadContactImportUnimported(task.id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `联系人批量导入-未导入名单-${task.taskNo}.xlsx`;
      anchor.click();
      URL.revokeObjectURL(url);
      return blob;
    });
  }, [run, task]);

  const updateCell = useCallback(
    (
      rowId: number,
      group: "organization" | "contact" | "record" | "assignment",
      field: string,
      value: unknown,
    ) => {
      setRows((current) => updateContactImportCell(current, rowId, group, field, value));
      setTask((current) => current ? { ...current, previewDigest: null } : current);
    },
    [],
  );

  const updateAction = useCallback((rowId: number, actionType: ContactImportAction) => {
    setTask((current) => current ? { ...current, previewDigest: null } : current);
    setRows((current) =>
      current.map((row) =>
        row.id === rowId
          ? {
              ...row,
              actionType: row.matchResult.create_blocked && actionType === "create" ? "skip" : actionType,
            }
          : row,
      ),
    );
  }, []);

  const toggleRow = useCallback((rowId: number, selected: boolean) => {
    setRows((current) =>
      current.map((row) =>
        row.id === rowId && isContactImportRowExecutable(row) ? { ...row, selected } : row,
      ),
    );
  }, []);

  const executableCount = useMemo(() => selectExecutableRowIds(rows).length, [rows]);

  return {
    task,
    rows,
    busy,
    error,
    result,
    executableCount,
    uploadFile,
    parsePaste,
    saveRows,
    execute,
    downloadTemplate,
    downloadUnimportedRows,
    updateCell,
    updateAction,
    toggleRow,
  };
}
